import React from 'react'
import { Button } from "components/Form"
import { UserRM } from "models/RM"
import { useCallback, useContext } from "react"
import { useSelector, useDispatch } from "react-redux"
import { initialize, isValid, submit } from "redux-form"
import { LightButton } from "styles/GlobalStyle"
import { UserContext } from "../UserContext"
import { useMutation } from "@tanstack/react-query"
import { createCustomUserFetch, updateCustomUserFetch } from "store/middlewares/user.crud"
import { toast } from "react-toastify"
import { toastConfig } from "config"
import { useNavigate, useParams } from "react-router-dom"
import { app } from "router/routes"
import { uniqueId } from "lodash"
import { affiliationGroups } from 'keys'
import roles, { allRoles, roleItems } from 'keys/roles'

const useSubmitBtns = () => {
    const { tzApproved, formname, setUserRoles, userRoles, isNewUser } = useContext(UserContext)
    const formValues = useSelector(state => state.form[formname]?.values)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const isFormValid = useSelector(state => isValid(formname)(state));
    const { affiliationID } = useParams()

    const resetForm = useCallback(() => {
        const id = formValues?.id
        setUserRoles([{ id: 1, affiliation: null, roles: [{ id: uniqueId() }] }])
        dispatch(initialize(formname, { id: id }));
    }, [formValues])

    const { mutateAsync: createCustomUser, isPending: createUserPending } = useMutation({
        mutationFn: createCustomUserFetch,
        onSuccess: () => {
            toast('המשתמש נוצר בהצלחה', toastConfig)
        }
    })

    const { mutateAsync: updateCustomUser, isPending: updateUserPending } = useMutation({
        mutationFn: updateCustomUserFetch,
        onSuccess: () => {
            toast('המשתמש עודכן בהצלחה', toastConfig)
        }
    })

    const validateUserRoles = useCallback(() => {
        let errorFlag = false

        userRoles.forEach(userRole => {
            const affiliationGroup = userRole?.affiliation?.group_name
            const isSchoolGroup = affiliationGroup == affiliationGroups.schools
            userRole.roles.forEach((affRole) => {
                const { data, role } = affRole
                if (!role)
                    errorFlag = true
                data?.forEach((item) => {
                    if (allRoles[role.name] == roles.teacher) {
                        if (isSchoolGroup) {
                            if (!item.grade || !item.classes)
                                errorFlag = true
                        }
                        else {
                            if (!item.learning_groups)
                                errorFlag = true
                        }
                    }
                    else if (allRoles[role.name] == roles.coordinator) {
                        if (isSchoolGroup && !item.grades)
                            errorFlag = true
                    }
                    else if (allRoles[role.name] == roles.single) {
                        if (isSchoolGroup)
                            if (!item.grade || !item.school_class || !item.test_takers)
                                errorFlag = true
                    }
                })
            })
        });
        return !errorFlag
    }, [userRoles])

    const saveUserData = useCallback(async () => {
        const userRolesValid = validateUserRoles(userRoles)

        if (!userRolesValid || !isFormValid) {
            if (!userRolesValid)
                toast.error('לא מלאת את כל הפרטים הדרושים בפרטי התפקידים', toastConfig)

            if (!isFormValid) {
                dispatch(submit(formname)) // Trigger validation for the current step
            }
            return false
        }

        else if (!tzApproved) {
            toast.error('אנא לחצי על אתר מספר זהות כדי שנוכל להמשיך', toastConfig)
            return
        }
        else {
            const user = new UserRM(formValues, userRoles, affiliationID)
            if (isNewUser) {
                const data = await createCustomUser(user)
                return data
            }
            else {
                const data = await updateCustomUser(user)
                return data
            }
        }
    }, [isNewUser, tzApproved, isFormValid, formValues, userRoles])

    const saveUserAndGo = useCallback(async () => {
        const data = await saveUserData()
        if (data) {
            // כשמגיעים דרך בית ספר ספציפי בטאב בתי ספר = חוזרים לצוות הבית ספר
            if (affiliationID)
                navigate(`/affiliations/${affiliationID}`, {
                    state: {
                        tabIndex: 2
                    }
                })
            // כשמגיעים מטאב משתמשים חוזרים למשתמשים
            else navigate(app.users)
            resetForm()
        }
        else return
    }, [affiliationID, isNewUser, tzApproved, isFormValid, formValues, userRoles])

    const submitBtns = React.useMemo(
        () => [
            {
                label: "ניקוי",
                action: resetForm,
                component: LightButton,
                size: "small",
                variant: "solid"
            },
            {
                label: isNewUser ? "שמור משתמש חדש" : "עדכן משתמש",
                action: saveUserAndGo,
                component: Button,
                disabled: createUserPending || updateUserPending,
                loading: createUserPending || updateUserPending,
                color: "dark",
                size: "small",
                type: "submit",
            }
        ], [createUserPending, updateUserPending, affiliationID, tzApproved, isNewUser, isFormValid, formValues, userRoles]
    )

    return submitBtns

}

export default useSubmitBtns    